import { memo, type CSSProperties } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { selectFloorLabelById } from '@store/selectors/monitor.floors.selectors';
import type { IMonitorReplay, IReplayEvent } from '@interfaces/IMonitorApi';
import { clusterMarkers, EVENT_KINDS, kindSpecOf, paintLayer, type IEventCopyContext, type IPlacedEvent } from './markers';
import { dayScale, siteClock } from './trackScale';
import styles from './ReplayTrack.module.css';

export interface ITrackMarkersProps {
  replay: IMonitorReplay;
  /** This row's events, time-sorted (see placeEvents); keep it referentially stable. */
  placed: IPlacedEvent[];
  tz: string;
  /** The track's rendered width; markers closer than about CLUSTER_PX on it merge. */
  widthPx: number;
  onMarker(event: IReplayEvent): void;
}

/** Markers closer than this on the rendered track merge into one. */
const CLUSTER_PX = 16;
/** A merged marker grows by about this much per extra digit of its count. */
const DIGIT_PX = 5;
/** How close an event must be to join a cluster of `size`, so count pills never overlap. */
const reachPx = (size: number) => CLUSTER_PX + DIGIT_PX * (String(size).length - 1);
/** A marker's rendered width: the 18 px disc, widened by a merged count's extra digits. */
const markerPx = (size: number) => (size > 1 ? 18 + DIGIT_PX * (String(size).length - 1) : 18);
/** A merged marker's tooltip lists at most this many events, then "+N more". */
const MAX_LINES = 8;

/**
 * One row of the day's event markers. Memoized on the events, the zone and the width, so a
 * playback tick (which only moves the playhead) never re-renders them: a real
 * day carries hundreds of events, each with a tooltip.
 */
export const TrackMarkers = memo(function TrackMarkers({ replay, placed, tz, widthPx, onMarker }: ITrackMarkersProps) {
  const { t } = useTranslation();
  const floorLabel = useSelector(selectFloorLabelById);
  const { percentAt } = dayScale(replay);
  const copyContext: IEventCopyContext = { t, equipment: replay.equipment, floorLabel };
  const where = (ids: string[]) =>
    ids.length > 1 ? [`${floorLabel(ids[0] as string)}–${floorLabel(ids.at(-1) as string)}`] : ids.map(floorLabel);
  const describe = ({ event, ms }: IPlacedEvent) => {
    const spec = kindSpecOf(event);
    return [siteClock(ms, tz), spec.title(event, copyContext), ...where(spec.floorIds(event))].join(' · ');
  };

  const clusters = clusterMarkers(placed, (ms) => (percentAt(ms) / 100) * widthPx, reachPx, markerPx, widthPx);

  return clusters.map(({ events, kind, drawX }, i) => {
    const first = events[0] as IPlacedEvent;
    const merged = events.length > 1;
    const more = events.length - MAX_LINES;
    const label = merged
      ? t('monitor.replay.markers.cluster', {
          count: events.length,
          from: siteClock(first.ms, tz),
          to: siteClock((events.at(-1) as IPlacedEvent).ms, tz),
        })
      : describe(first);
    const lines = events.slice(0, MAX_LINES).map(describe);
    if (more > 0) lines.push(t('monitor.replay.markers.more', { count: more }));
    return (
      <Tooltip
        key={`${kind}-${i}`}
        variant="marker"
        content={merged ? <TooltipContent title={label} lines={lines} /> : label}
      >
        <button
          type="button"
          className={merged ? `${styles.marker} ${styles.cluster}` : styles.marker}
          style={{ left: `${(drawX / widthPx) * 100}%`, '--c': EVENT_KINDS[kind].color, '--layer': paintLayer(kind) } as CSSProperties}
          aria-label={label}
          data-testid="replay-marker"
          onPointerDown={(ev) => ev.stopPropagation()}
          onPointerUp={(ev) => ev.stopPropagation()}
          onClick={() => onMarker(first.event)}
        >
          {merged ? events.length : EVENT_KINDS[kind].glyph}
        </button>
      </Tooltip>
    );
  });
});
